import React from 'react';
import {
    Box,
    Container,
    Grid,
    Typography,
    Button,
    Stack,
    IconButton
} from '@mui/material';
import {
    Twitter as TwitterIcon,
    Instagram as InstagramIcon
} from '@mui/icons-material';

const LIGHT_PURPLE = '#A76FFF';

interface AppFooterProps {
    /**
     * Optional background color override
     * @default '#1a1a1a'
     */
    bgcolor?: string;
    /**
     * Optional text color override
     * @default 'white'
     */
    color?: string;
}

/**
 * Reusable footer component used across all public-facing pages
 * Includes navigation links, social media icons, and copyright information
 */
const AppFooter: React.FC<AppFooterProps> = ({
    bgcolor = '#1a1a1a',
    color = 'white'
}) => {
    return (
        <Box sx={{
            py: 6,
            bgcolor,
            color
        }}>
            <Container maxWidth="lg">
                <Grid container spacing={4}>
                    {/* Index Column */}
                    <Grid item xs={6} md={4}>
                        <Typography variant="h6" sx={{ mb: 3, color: '#888', fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '1px' }}>
                            Index
                        </Typography>
                        <Stack spacing={2}>
                            <Button
                                component="a"
                                href="/about-us"
                                sx={{
                                    justifyContent: 'flex-start',
                                    color,
                                    textTransform: 'none',
                                    fontSize: { xs: '0.875rem', md: '1rem' },
                                    fontWeight: 500,
                                    p: 0,
                                    '&:hover': { bgcolor: 'transparent', color: LIGHT_PURPLE }
                                }}
                            >
                                Our Story
                            </Button>
                            <Button
                                component="a"
                                href="/blog"
                                sx={{
                                    justifyContent: 'flex-start',
                                    color,
                                    textTransform: 'none',
                                    fontSize: { xs: '0.875rem', md: '1rem' },
                                    fontWeight: 500,
                                    p: 0,
                                    '&:hover': { bgcolor: 'transparent', color: LIGHT_PURPLE }
                                }}
                            >
                                Blog
                            </Button>
                            <Button
                                component="a"
                                href="/features"
                                sx={{
                                    justifyContent: 'flex-start',
                                    color,
                                    textTransform: 'none',
                                    fontSize: { xs: '0.875rem', md: '1rem' },
                                    fontWeight: 500,
                                    p: 0,
                                    '&:hover': { bgcolor: 'transparent', color: LIGHT_PURPLE }
                                }}
                            >
                                Features
                            </Button>
                            <Button
                                onClick={() => window.location.href = 'mailto:hello@poptheballoonapp.com'}
                                sx={{
                                    justifyContent: 'flex-start',
                                    color,
                                    textTransform: 'none',
                                    fontSize: { xs: '0.875rem', md: '1rem' },
                                    fontWeight: 500,
                                    p: 0,
                                    '&:hover': { bgcolor: 'transparent', color: LIGHT_PURPLE }
                                }}
                            >
                                Contact
                            </Button>
                            {/* Social Icons */}
                            <Stack direction="row" spacing={2} sx={{ pt: 1 }}>
                                <IconButton
                                    component="a"
                                    href="https://twitter.com/ptbdating"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                        color,
                                        p: 0,
                                        '&:hover': { color: LIGHT_PURPLE }
                                    }}
                                >
                                    <TwitterIcon />
                                </IconButton>
                                <IconButton
                                    component="a"
                                    href="https://www.instagram.com/PopTheballoonDating"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                        color,
                                        p: 0,
                                        '&:hover': { color: LIGHT_PURPLE }
                                    }}
                                >
                                    <InstagramIcon />
                                </IconButton>
                            </Stack>
                        </Stack>
                    </Grid>

                    {/* Resources Column */}
                    <Grid item xs={6} md={4}>
                        <Typography variant="h6" sx={{ mb: 3, color: '#888', fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '1px' }}>
                            Resources
                        </Typography>
                        <Stack spacing={2}>
                            <Button
                                component="a"
                                href="/dating-safety"
                                sx={{
                                    justifyContent: 'flex-start',
                                    color,
                                    textTransform: 'none',
                                    fontSize: { xs: '0.875rem', md: '1rem' },
                                    fontWeight: 500,
                                    p: 0,
                                    '&:hover': { bgcolor: 'transparent', color: LIGHT_PURPLE }
                                }}
                            >
                                Safe Dating Tips
                            </Button>
                            <Button
                                component="a"
                                href="/child-safety"
                                sx={{
                                    justifyContent: 'flex-start',
                                    color,
                                    textTransform: 'none',
                                    fontSize: { xs: '0.875rem', md: '1rem' },
                                    fontWeight: 500,
                                    p: 0,
                                    '&:hover': { bgcolor: 'transparent', color: LIGHT_PURPLE }
                                }}
                            >
                                Child Safety
                            </Button>
                            <Button
                                component="a"
                                href="/faq"
                                sx={{
                                    justifyContent: 'flex-start',
                                    color,
                                    textTransform: 'none',
                                    fontSize: { xs: '0.875rem', md: '1rem' },
                                    fontWeight: 500,
                                    p: 0,
                                    '&:hover': { bgcolor: 'transparent', color: LIGHT_PURPLE }
                                }}
                            >
                                FAQ
                            </Button>
                        </Stack>
                    </Grid>

                    {/* Legal Column */}
                    <Grid item xs={12} md={4}>
                        <Typography variant="h6" sx={{ mb: 3, color: '#888', fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '1px' }}>
                            Legal
                        </Typography>
                        <Stack spacing={2}>
                            <Button
                                component="a"
                                href="/terms-of-service"
                                sx={{
                                    justifyContent: 'flex-start',
                                    color,
                                    textTransform: 'none',
                                    fontSize: { xs: '0.875rem', md: '1rem' },
                                    fontWeight: 500,
                                    p: 0,
                                    '&:hover': { bgcolor: 'transparent', color: LIGHT_PURPLE }
                                }}
                            >
                                Terms
                            </Button>
                            <Button
                                component="a"
                                href="/privacy-policy"
                                sx={{
                                    justifyContent: 'flex-start',
                                    color,
                                    textTransform: 'none',
                                    fontSize: { xs: '0.875rem', md: '1rem' },
                                    fontWeight: 500,
                                    p: 0,
                                    '&:hover': { bgcolor: 'transparent', color: LIGHT_PURPLE }
                                }}
                            >
                                Privacy
                            </Button>
                            <Button
                                component="a"
                                href="/cookie-policy"
                                sx={{
                                    justifyContent: 'flex-start',
                                    color,
                                    textTransform: 'none',
                                    fontSize: { xs: '0.875rem', md: '1rem' },
                                    fontWeight: 500,
                                    p: 0,
                                    '&:hover': { bgcolor: 'transparent', color: LIGHT_PURPLE }
                                }}
                            >
                                Cookie Policy
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>

                {/* Copyright */}
                <Box sx={{ mt: 6, pt: 4, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                    <Typography variant="body2" sx={{ color: '#888', textAlign: 'center' }}>
                        © {new Date().getFullYear()} Pop The Balloon. All rights reserved.
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default AppFooter;
